import * as React from 'react'
import * as Yup from 'yup'
import * as RT from '../../config/routes'
import { Formik, Form } from 'formik'
import { useDispatch } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { styled, useTheme } from '@mui/material/styles'
import { activated, setRequiresPasswordChange, setAuthSession } from '../../redux/slices/Auth/login'
import { showWelcome } from '../../redux/slices/Welcome/welcomeSlice'
import { show as showDrawerRight } from '../../redux/slices/Drawer/drawerRight'
import { reloadMapDataForSession } from '../../redux/slices/Tree/treeFeatures'
import { Grid, Box, SvgIcon, Link, Alert } from '@mui/material'
import { InputAdornment, IconButton, Button } from '@mui/material'
import { Lock, AccountCircle, Visibility, VisibilityOff } from '@mui/icons-material'
import { ReactComponent as LoginImage } from '../../assets/images/Login.svg'
import { isUserVerify, login } from '../../services'
import { useCities } from '../../context/CitiesContext'
import { buildCantonUrl } from '../../utils/cantonUrl'
import { PUBLIC_SIGNUP_ENABLED } from '../../config/featureFlags'

import { INITIAL_FORM_STATE } from '../../utils/formikUpdate'
import { schema } from '../../utils/schemaValidation'
import Textfield from '../FormsUI/Textfield'

const styleSvg = { width: 300, height: 220 }

const ButtonCustom = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  textTransform: 'capitalize',
  fontFamily: theme.typography.fontFamily,
  fontStyle: 'normal',
  fontWeight: 600,
  lineHeight: '22px',
  fontSize: '0.9em',
  '&:hover': {
    backgroundColor: theme.palette.primary.light,
  },
  '&:active': {
    boxShadow: 'none',
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
  },
}))

const centerModalStyles = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
}

const boxStyle = {
  width: '100%',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: '25px',
  textAlign: 'center',
  borderRadius: '14px',
}

const Login = React.forwardRef(({ modal, icon = true }, ref) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const { getDefaultCity, getEnabledCantones } = useCities()
  const cantones = getEnabledCantones()
  const styleLinkText = {
    color: theme.palette.primary.main,
    fontFamily: theme.typography.fontFamily,
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '0.92em',
    lineHeight: '12px',
    textDecoration: 'none',
    '&:hover': {
      color: theme.palette.primary.main,
      textDecoration: 'underline',
      fontWeight: 600,
      cursor: 'pointer',
    },
  }

  const [showPassword, setShowPassword] = React.useState(false)
  const [alert, setAlert] = React.useState(null)

  const handleClickShowPassword = () => setShowPassword(!showPassword)
  const handleMouseDownPassword = event => event.preventDefault()
  const handleForgotPassword = () => {
    dispatch(showDrawerRight(false))
    dispatch(activated(false))
    navigate(RT.FORGOT_PASSWORD_ROUTE)
  }
  const handleRegisterUser = () => {
    dispatch(showDrawerRight(false))
    dispatch(activated(false))
    navigate(RT.REGISTER_USER_ROUTE)
  }

  const styleB = modal ? { ...boxStyle, ...centerModalStyles } : boxStyle

  const handleClickLogin = async ({ email, password_login: password }) => {
    const response = await login({ email, password })

    if (!response.error) {
      if (response.accountLogin === 'rejected')
        setAlert({
          type: 'warning',
          message:
            'Verifique su correo electrónico o contraseña. Credenciales incorrectas.',
        })

      if (response.accountLogin === 'pending')
        setAlert({ type: 'info', message: response.message })

      if (response.accountLogin === 'approved') {
        window.localStorage.setItem('ArbolEc_token', response.token)
        if (response.contrasena_temporal) dispatch(setRequiresPasswordChange(true))

        // Esperar a que isUserVerify resuelva para tener assignedTenants disponibles
        let userLocal = null
        try {
          userLocal = await isUserVerify(`Bearer ${response.token}`)
          dispatch(setAuthSession(userLocal))
          // El popup depende de la sesión: se recarga el mapa sin los datos cargados como anónimo
          dispatch(reloadMapDataForSession())
        } catch (err) {
          console.warn('Error al verificar usuario:', err)
        }

        // Calcular cantón destino desde los datos del usuario recién verificado
        const user = userLocal?.user
        const assignedTenants = user?.assignedTenants || []
        const isResearcher = user?.rol?.includes('investigador')

        const targetCanton = (() => {
          if (isResearcher && assignedTenants.length > 0) {
            const primary = assignedTenants.find(t => t.isPrimary)
            return primary?.cantonSlug || assignedTenants[0]?.cantonSlug
          }
          return getDefaultCity()?.slug || 'loja'
        })()

        // Si el usuario venía de una vista de mapa, volver a ese cantón
        const returnCanton = location.state?.returnCanton
        const destination = buildCantonUrl(returnCanton || targetCanton, cantones)

        dispatch(showWelcome())
        navigate(destination)
      }
      return
    }
    setAlert({ type: 'error', message: response.message })
  }

  const FORM_VALIDATION = Yup.object().shape({
    ...schema.EMAIL,
    ...schema.PASSWORD_LOGIN,
  })

  return (
    <Box sx={styleB} ref={ref}>
      {icon && (
        <Box>
          <SvgIcon sx={styleSvg} component={LoginImage} inheritViewBox />
        </Box>
      )}
      <Box>
        <Formik
          initialValues={{ ...INITIAL_FORM_STATE }}
          validationSchema={FORM_VALIDATION}
          onSubmit={async values => await handleClickLogin(values)}>
          <Form>
            <Grid container spacing={2} sx={{ pt: 0, width: '100%', ml: 0 }}>
              <Grid item xs={12} style={{ paddingLeft: 0 }}>
                <Textfield
                  name="email"
                  size="small"
                  label="Ingrese su correo electrónico"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccountCircle />
                      </InputAdornment>
                    ),
                  }}
                  placeholder="user@example.com"
                />
              </Grid>
              <Grid item xs={12} style={{ paddingLeft: 0 }}>
                <Textfield
                  name="password_login"
                  size="small"
                  label="Ingrese su contraseña"
                  type={showPassword ? 'text' : 'password'}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end">
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  placeholder="password"
                />
              </Grid>
              {alert && (
                <Grid item xs={12} style={{ paddingLeft: 0 }}>
                  <Alert severity={alert.type}>{alert.message}</Alert>
                </Grid>
              )}
              <Grid item xs={12} style={{ paddingLeft: 0 }}>
                <ButtonCustom variant="contained" fullWidth type="submit">
                  Iniciar Sesión
                </ButtonCustom>
              </Grid>
              <Grid
                item
                xs={PUBLIC_SIGNUP_ENABLED ? 8 : 12}
                style={{ paddingLeft: 0, textAlign: PUBLIC_SIGNUP_ENABLED ? 'start' : 'center' }}>
                <Link onClick={handleForgotPassword} sx={styleLinkText}>
                  ¿Olvidaste tu contraseña?
                </Link>
              </Grid>
              {PUBLIC_SIGNUP_ENABLED && (
                <Grid item xs={4} style={{ paddingLeft: 0, textAlign: 'end' }}>
                  <Link onClick={handleRegisterUser} sx={styleLinkText}>
                    Crear cuenta
                  </Link>
                </Grid>
              )}
            </Grid>
          </Form>
        </Formik>
      </Box>
    </Box>
  )
})

export default Login
